import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Select, Space, Tag, Tooltip, Typography } from 'antd';
import { InfoCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { useDispatch, useIntl, useSelector } from 'umi';
import isEmpty from 'lodash/isEmpty';
import classNames from 'classnames';
import { ProForm } from '@ant-design/pro-components';

import { AssigneeModel } from '@/typings/models/Assignee';
import { ServiceModel } from '@/typings/models/Location';
import { F, getIntlTranslation, INTL_ACTION, INTL_ENTITY } from '@/utils/intl';
import { updateUserRequest } from '@/services/api/user';
import { processAPIResponseNow } from '@/utils/request';
import * as LocationSelectors from '@/selectors/locationSelectors';
import { useHasAccess } from '@/utils/hooks';
import PERMISSIONS from '@/constants/permissions';
import BranchServiceTreeSelect from '@/components/Filters/BranchServiceSelect';
import { usePubSub } from '@/context/PubSubContext';
import { SERVICE_AGENTS_CHANGED } from '@/context/PubSubContext/events';
import styles from './AgentDrawer.less';

const { Title } = Typography;

/**
 * Opens the global branch drawer (see DvaWatcher) on a branch or focused on
 * one of its services, permission-gated like the ActionTag equivalents.
 */
export function useOpenLocationDrawer() {
  const dispatch = useDispatch();
  const canOpenBranch = useHasAccess([PERMISSIONS.Branches.EDIT, PERMISSIONS.BUSINESS_PARTNER]);
  const canOpenService = useHasAccess([PERMISSIONS.BranchServices.EDIT, PERMISSIONS.BUSINESS_PARTNER]);

  const openBranch = (branch?: { id?: string }) => {
    if (!canOpenBranch || !branch?.id) return;
    dispatch({ type: 'location/getBranch', payload: { branchId: branch.id } });
    dispatch({ type: 'location/toggleBranchDrawer', payload: { branch } });
  };

  const openService = (service?: ServiceModel) => {
    if (!canOpenService || !service?.branch?.id) return;
    dispatch({ type: 'location/getBranch', payload: { branchId: service.branch.id } });
    dispatch({
      type: 'location/toggleBranchDrawer',
      payload: { branch: service.branch, service },
    });
  };

  return { canOpenBranch, canOpenService, openBranch, openService };
}

/**
 * Shared state + handlers for the agent's management assignments:
 * - branch access (branches_users pivot — membership means branch manager)
 * - managed services (branch_services_managers pivot)
 * Both are written through PATCH /users/:id as full id lists.
 */
export function useManagementAssignments(agent: AssigneeModel, onDirty?: () => void) {
  const dispatch = useDispatch();
  const intl = useIntl();
  const { publish } = usePubSub();
  const canEdit = useHasAccess([PERMISSIONS.Users.EDIT, PERMISSIONS.BUSINESS_PARTNER]);
  const allBranches = useSelector(LocationSelectors.selectAllBranchesList);

  useEffect(() => {
    if (isEmpty(allBranches)) {
      dispatch({ type: 'location/getAllBranchList' });
    }
  }, []);

  const managedBranchIds = useMemo(
    () => agent.branches?.map((b) => b.id) || [],
    [agent.branches],
  );
  const managedServiceIds = useMemo(
    () => agent.managed_services?.map((s) => s.id) || [],
    [agent.managed_services],
  );

  const managedBranches = useMemo(
    () => [...(agent.branches || [])].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '')),
    [agent.branches],
  );
  const managedServices = useMemo(
    () =>
      [...(agent.managed_services || [])].sort((a, b) =>
        `${a.branch?.name ?? ''} ${a.service_type?.name ?? ''}`.localeCompare(
          `${b.branch?.name ?? ''} ${b.service_type?.name ?? ''}`,
        ),
      ),
    [agent.managed_services],
  );

  const branchOptions = useMemo(
    () =>
      (allBranches || [])
        .filter((b) => b.is_active !== false && !managedBranchIds.includes(b.id))
        .map((b) => ({ label: b.name, value: b.id })),
    [allBranches, managedBranchIds],
  );

  const patchAssignments = (
    payload: Record<string, (string | undefined)[]>,
    // Service ids whose agent list changed. On a removal the id is already gone from
    // `payload.services`, so the caller passes the union of before and after.
    touchedServiceIds?: (string | undefined)[],
  ) => {
    if (!agent.id) return;
    onDirty?.();
    updateUserRequest({
      params: { userId: agent.id },
      payload,
    }).then((response) => {
      processAPIResponseNow(
        response,
        {
          showSuccessMessage: true,
          successEntity: INTL_ENTITY.AGENT,
          onSuccess() {
            dispatch({
              type: 'user/fetchSingleAgent',
              payload: { agentId: agent.id },
            });
            // Bookable services only — the calendar reads `service.agents`, so
            // branch access and service managers change nothing there. Presence, not
            // length: removing the last service sends `services: []` and still counts.
            if (payload.services) {
              publish(SERVICE_AGENTS_CHANGED, {
                serviceIds: touchedServiceIds ?? payload.services,
              });
            }
          },
        },
        'data',
      );
    });
  };

  const addBranchAccess = (branchIds: string[]) => {
    if (branchIds.length === 0) return;
    patchAssignments({ branches: [...managedBranchIds, ...branchIds] });
  };

  const removeBranchAccess = (branchId: string) => {
    Modal.confirm({
      title: intl.formatMessage({
        id: 'pages.users.managedBranches.removeConfirm',
        defaultMessage: 'Remove branch access for this user?',
      }),
      okText: getIntlTranslation(intl, { entity: INTL_ENTITY.BRANCH, action: INTL_ACTION.REMOVE }),
      cancelText: intl.formatMessage({ id: 'common.actions.cancel', defaultMessage: 'Cancel' }),
      onOk: () => patchAssignments({ branches: managedBranchIds.filter((id) => id !== branchId) }),
    });
  };

  const addManagedServices = (serviceIds: string[]) => {
    if (serviceIds.length === 0) return;
    patchAssignments({ managed_services: [...managedServiceIds, ...serviceIds] });
  };

  const removeManagedService = (serviceId: string) => {
    Modal.confirm({
      title: intl.formatMessage({
        id: 'pages.users.managedServices.removeConfirm',
        defaultMessage: 'Remove this user as manager of this service?',
      }),
      okText: getIntlTranslation(intl, { entity: INTL_ENTITY.SERVICE, action: INTL_ACTION.REMOVE }),
      cancelText: intl.formatMessage({ id: 'common.actions.cancel', defaultMessage: 'Cancel' }),
      onOk: () => patchAssignments({ managed_services: managedServiceIds.filter((id) => id !== serviceId) }),
    });
  };

  return {
    canEdit,
    managedBranchIds,
    managedServiceIds,
    managedBranches,
    managedServices,
    branchOptions,
    patchAssignments,
    addBranchAccess,
    removeBranchAccess,
    addManagedServices,
    removeManagedService,
  };
}

export type ManagementAssignments = ReturnType<typeof useManagementAssignments>;

export const AddBranchAccessSelect: React.FC<{ management: ManagementAssignments }> = ({ management }) => {
  const [pending, setPending] = useState<string[]>([]);

  return (
    <div className={styles.addService}>
      <Select
        mode="multiple"
        variant="borderless"
        size="large"
        showSearch
        optionFilterProp="label"
        placeholder={
          <Space>
            <PlusOutlined />
            <F entity={INTL_ENTITY.BRANCH} action={INTL_ACTION.ADD} />
          </Space>
        }
        value={pending}
        onChange={setPending}
        options={management.branchOptions}
        onDropdownVisibleChange={(visible: boolean) => {
          if (!visible && pending.length > 0) {
            management.addBranchAccess(pending);
            setPending([]);
          }
        }}
      />
    </div>
  );
};

export const AddManagedServiceSelect: React.FC<{ management: ManagementAssignments }> = ({ management }) => {
  const [form] = ProForm.useForm();

  return (
    <ProForm form={form} submitter={false}>
      <div className={styles.addService}>
        <BranchServiceTreeSelect
          placeholder={
            <Space>
              <PlusOutlined />
              <F entity={INTL_ENTITY.SERVICE} action={INTL_ACTION.ADD} />
            </Space>
          }
          name="managed_services"
          inScope={true}
          filterServices={management.managedServiceIds as string[]}
          selectProps={{
            mode: 'multiple',
            variant: 'borderless',
            size: 'large',
          }}
          fieldProps={{
            onDropdownVisibleChange: (visible: boolean) => {
              if (!visible) {
                const values = form.getFieldValue('managed_services');
                if (values && values.length > 0) {
                  management.addManagedServices(values);
                  form.setFieldsValue({ managed_services: [] });
                }
              }
            },
          }}
        />
      </div>
    </ProForm>
  );
};

const BlockTitle: React.FC<{ title: React.ReactNode; tooltip: React.ReactNode }> = ({ title, tooltip }) => (
  <Space size={4} align="center" className={styles.managementBlockTitle}>
    <Title level={5}>{title}</Title>
    <Tooltip title={tooltip}>
      <InfoCircleOutlined />
    </Tooltip>
  </Space>
);

export const ManagedBranchesBlock: React.FC<{ management: ManagementAssignments }> = ({ management }) => {
  const { canOpenBranch, openBranch } = useOpenLocationDrawer();

  return (
    <div className={styles.managementBlock}>
      <BlockTitle
        title={<F id="pages.users.managedBranches.title" defaultMessage="Branch Manager" />}
        tooltip={
          <F
            id="pages.users.managedBranches.tooltip"
            defaultMessage="User with branch access can see all leads, calendar entries and users at the branch. Required for branch managers."
          />
        }
      />
      <div className={styles.managementTags}>
        {management.managedBranches.map((branch) => (
          <Tag
            key={branch.id}
            className={classNames({ [styles.clickableTag]: canOpenBranch })}
            onClick={() => openBranch(branch)}
            closable={management.canEdit}
            onClose={(e) => {
              e.preventDefault();
              e.stopPropagation();
              management.removeBranchAccess(branch.id!);
            }}
          >
            {branch.name}
          </Tag>
        ))}
      </div>
      {management.canEdit && <AddBranchAccessSelect management={management} />}
    </div>
  );
};

export const ManagedServicesBlock: React.FC<{ management: ManagementAssignments }> = ({ management }) => {
  const { canOpenService, openService } = useOpenLocationDrawer();

  return (
    <div className={styles.managementBlock}>
      <BlockTitle
        title={<F id="pages.users.managedServices.title" defaultMessage="Service Manager" />}
        tooltip={
          <F
            id="pages.locations.branchServiceManagerTooltip"
            defaultMessage="The manager of the service team receives access to calendars and leads of the team."
          />
        }
      />
      <div className={styles.managementTags}>
        {management.managedServices.map((service) => (
          <Tag
            key={service.id}
            className={classNames({ [styles.clickableTag]: canOpenService })}
            onClick={() => openService(service)}
            closable={management.canEdit}
            onClose={(e) => {
              e.preventDefault();
              e.stopPropagation();
              management.removeManagedService(service.id!);
            }}
          >
            {service.service_type?.icon} {service.branch?.name} - {service.service_type?.name}
          </Tag>
        ))}
      </div>
      {management.canEdit && <AddManagedServiceSelect management={management} />}
    </div>
  );
};

/**
 * Composed management view: the "Management" section of the Services & Access
 * drawer tab.
 */
const AgentManagementPanel: React.FC<{ agent: AssigneeModel; onDirty?: () => void }> = ({ agent, onDirty }) => {
  const management = useManagementAssignments(agent, onDirty);

  return (
    <>
      <ManagedBranchesBlock management={management} />
      <ManagedServicesBlock management={management} />
    </>
  );
};

export default AgentManagementPanel;
